import { Box, CircularProgress } from '@mui/material'
import { Hub } from 'aws-amplify'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { isFullUrl } from '@app/util'
import { useResolveSSOProfile } from '../../hooks/useResolveSSOProfile'

/**
 * Landing route for the Cognito Hosted UI (Managed Login / SSO) redirect.
 * Amplify's `Hub.listen('auth', ...)` wiring in AuthProvider completes the
 * sign-in; this page then bridges the Cognito identity to Connect (via the
 * shared `useResolveSSOProfile` hook) and routes onward - to
 * `/complete-profile` (new or incomplete profiles) or the original
 * destination.
 */
export const SsoCallback: FC = () => {
  const navigate = useNavigate()
  const [redirectTo, setRedirectTo] = useState<string>()

  // `federatedSignIn`'s `customState` (set by ManagedLoginRedirect) is
  // delivered via a separate `customOAuthState` Hub event, dispatched
  // synchronously right after `cognitoHostedUI`.
  useEffect(() => {
    return Hub.listen('auth', ({ payload }) => {
      if (payload.event !== 'customOAuthState') return

      try {
        const { redirectTo: parsedRedirectTo } = JSON.parse(
          payload.data as string,
        ) as { redirectTo?: string }
        if (parsedRedirectTo) setRedirectTo(parsedRedirectTo)
      } catch {
        // Malformed/missing custom state - fall back to the default redirect.
      }
    })
  }, [])

  const { cognitoIdentity, isProfileComplete, fetching, error } =
    useResolveSSOProfile()

  useEffect(() => {
    if (!cognitoIdentity || fetching) return

    if (error) {
      navigate('/login', { replace: true })
      return
    }

    if (!isProfileComplete) {
      navigate('/complete-profile', { replace: true, state: { redirectTo } })
      return
    }

    if (redirectTo && isFullUrl(redirectTo)) {
      window.location.href = redirectTo
      return
    }

    navigate(redirectTo || '/', { replace: true })
  }, [
    cognitoIdentity,
    fetching,
    error,
    isProfileComplete,
    redirectTo,
    navigate,
  ])

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={60} />
    </Box>
  )
}
